import { t, Trans } from '@lingui/macro'
import { Survey } from 'app/backend/salesforce-maps/Survey'
import { Question } from 'app/services/sfAuth/sfData/sfSurvey'
import { toLower, trim } from 'lodash'
import React from 'react'
import { myI18n } from '../../../translation/I18nConnectedProvider'
import {
  ScoreCardQuestion,
  scoreCardStructureBase
} from '../../services/sfAuth/sfData/sfScoreCard'
import { questionTypes } from './QuestionWidget'
import {
  checkSurveyQuestionsToDisable,
  nonApplicableSuffix
} from './SurveyTab'

interface SurveyMappingType {
  [key: string]: keyof ScoreCardQuestion
};

export interface ScoresData {
  sectionsCount: number
  sectionsScoreSum: number
  futureScoresSum: number
  lowestScore: number
  lowestScoreSection: string | string[]
  adjustmentText: React.ReactNode | null
  adjustmentValue: string | null
  themesAligned: number
  overallFutureScore: number
  averageScore: number
  scoresMatch: number
  understanding: string
  adjustmentPainText?: string
}

export const surveyMapping: SurveyMappingType = {
  id: 'QuestionID',
  group: 'Scoring group',
  option_0: 'Option 1',
  option_1: 'Option 2',
  option_2: 'Option 3',
  option_3: 'Option 4',
  option_4: 'Option 5',
  option_5: 'Option 6',
  option_6: 'Option 7',
  option_7: 'Option 8',
  option_8: 'Option 9',
  option_9: 'Option 10',
  option_10: 'Option 11',
  option_11: 'Option 12',
  modifier: 'modifier'
}

interface ScoringDetails {
  scoreGroup?: string
  [key: string]: any
  [groupKey: `group${number}`]: GroupScoring
}

interface GroupScoring {
  scoreGroup: string
  [key: string]: any
}

interface ScoreCardItem {
  surveyItemIdentId: string
  modifier: string
  scoreGroup: string
  scoringDetails: ScoringDetails
}

interface ScoreCardToStructureParams {
  scoreCardItems: ScoreCardItem[]
}

interface ScoreCardStructureBase {
  QuestionID?: string
  modifier?: string | number
  'Scoring group'?: string
  [key: string]: any
}

interface StructureResult {
  [questionId: string]: ScoreCardStructureBase
}

export const scoreCardToStructure = ({
  scoreCardItems
}: ScoreCardToStructureParams): StructureResult => {
  const toRet = {}
  scoreCardItems.forEach(item => {
    const struct = { ...scoreCardStructureBase }
    struct.QuestionID = item.surveyItemIdentId
    struct.modifier = item.modifier
    struct['Scoring group'] = item.scoreGroup
    if (item.scoringDetails != null &&
      typeof item.scoringDetails === 'object'
    ) {
      Object.keys(item.scoringDetails).forEach(key => {
        const value = item.scoringDetails[key]
        if (surveyMapping[key] != null) {
          struct[surveyMapping[key]] = value
        } else {
          struct[key] = value
        }
        if (key.includes('group')) {
          const index = key.slice(-1)
          const groupScoring = { ...struct }
          groupScoring.QuestionID += '.' + index
          groupScoring['Scoring group'] = value.scoreGroup
          Object.keys(value).forEach(key => {
            const gValue = value[key]
            if (surveyMapping[key] != null) {
              groupScoring[surveyMapping[key]] = gValue
            } else {
              groupScoring[key] = gValue
            }
          })
          Object.keys(groupScoring).forEach(key => {
            // Just for cleaner score card
            if (key.includes('group') && key !== 'Scoring group') {
              // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
              delete groupScoring[key]
            }
          })
          toRet[groupScoring.QuestionID] = groupScoring
        }
      })
    }
    if (item.surveyItemIdentId != null && item.surveyItemIdentId !== '') {
      toRet[item.surveyItemIdentId] = struct
    }
  })
  return toRet
}

const optionNames = Object.keys(surveyMapping).filter(item =>
  item.startsWith('option_')
)

export const getTextFromSectionsArray = ({
  data
}: {
  data: string | string[]
}): React.ReactElement | null => {
  if (typeof data === 'string') {
    return (
      <>
        {toLower(myI18n._(data))}.
      </>
    )
  } else if (Array.isArray(data) && data.length > 0) {
    return (
      <>
        {data.map((section, index) => (
          <React.Fragment key={index}>
            {toLower(myI18n._(section))}
            {index < data.length - 1 &&
              (index === data.length - 2 ? <> {myI18n._(t`and`)} </> : ', ')}
          </React.Fragment>
        ))}
        .
      </>
    )
  }
  return null
}

export const getPdfTextFromSectionsArray = ({
  data
}: { data: string | string[] }): string | null => {
  if (typeof data === 'string') {
    return `${toLower(myI18n._(data))}.`
  } else {
    if (Array.isArray(data)) {
      const formattedText = data.map((section, index) => {
        const sectionText = toLower(myI18n._(section))

        if (index === data.length - 1) {
          return sectionText
        } else if (index === data.length - 2) {
          return `${sectionText} ${myI18n._(t`and`)} `
        } else {
          return `${sectionText}, `
        }
      }).join('')

      return `${formattedText}.`
    }
    return ''
  }
}

export const surveyMaxOption = (question: ScoreCardQuestion): number => {
  let max = 0
  let optionValue: number
  const { isFilled, isNotFilled, rangeScoring, multipleAnswers } = question
  optionNames.forEach(item => {
    if (question[surveyMapping[item]] !== '') {
      optionValue = parseFloat(
        ((question[surveyMapping[item]] as string) + '').replace(',', '.')
      )
      if (!isNaN(optionValue)) {
        if (multipleAnswers == null) {
          max = Math.max(max, optionValue)
        } else {
          max += optionValue
        }
      }
    }
  })
  if ((isNotFilled != null && isNotFilled !== '') &&
    (isFilled != null && isFilled !== '')
  ) {
    const notValue = Number(isNotFilled)
    const value = Number(isFilled)
    return notValue > value ? notValue : value
  }
  if (rangeScoring != null && rangeScoring.length > 0) {
    rangeScoring.forEach(v => {
      if (Number(v) > max) {
        max = Number(v)
      }
    })
  }
  return max
}

const normalizeString = (value: string): string => trim(toLower(value))

interface CalculateScoreProps {
  survey: Survey
  surveyScoreCard: { [questionId: string]: ScoreCardQuestion }
  skippedSections: string[]
  sectionsData: Array<{
    id: string
    questions: Array<{
      id: string
      isRequired: boolean
      type: string
      [key: string]: any
    }>
  }>
  esat: boolean
}

interface ScoreSection {
  importancePercent?: number
  percent?: number
  satisfaction?: number
  satisfactionTotal?: number
  sum?: number
  total?: number
  understandingPercent?: number
  weight: number
  weightTotal: number
}

interface CalculatedScore {
  Future: {
    resilience: number
    viability: number
    vitality: number
  }
  adjustment: number
  sections: Record<string, ScoreSection>
}

export const calculateScore = ({
  survey,
  surveyScoreCard = {},
  skippedSections,
  sectionsData,
  esat
}: CalculateScoreProps): CalculatedScore => {
  const questionsMap = new Map<string, Question>()

  sectionsData.forEach(section => {
    section.questions.forEach(question => {
      questionsMap[question.id] = question
    })
  })
  const surveyAnswersRaw = JSON.parse(survey.answer)
  const disableIds = survey.containsAggragatedData
    ? []
    : checkSurveyQuestionsToDisable({
      data: sectionsData,
      values: surveyAnswersRaw
    })

  const sectionBase = {
    weight: 0,
    weightTotal: 0,
    sum: 0,
    total: 0,
    satisfaction: 0,
    satisfactionTotal: 0,
    percent: 0,
    importancePercent: 0,
    understandingPercent: 0
  }
  const oldSections = {
    governance: {
      ...sectionBase
    },
    property: {
      ...sectionBase
    },
    financial: {
      ...sectionBase
    },
    social: {
      ...sectionBase
    }
  }
  const calc: CalculatedScore = {
    adjustment: 0,
    sections: {},
    Future: {
      vitality: 0,
      viability: 0,
      resilience: 0
    }
  }

  // handle old sat score card
  if (!esat) {
    calc.sections = oldSections
  }
  for (const question in surveyScoreCard) {
    const sectionId = normalizeString(
      surveyScoreCard[question]['Scoring group'] ?? ''
    )
    if (
      calc.sections[sectionId] == null &&
      sectionId !== '' &&
      sectionId != null
    ) {
      calc.sections[sectionId] = { ...sectionBase }
    }
  }

  const surveyAnswers = {}
  for (const id in surveyAnswersRaw) {
    if (
      !id.includes(nonApplicableSuffix) &&
      id !== 'skippedSections' &&
      !disableIds.includes(id)
    ) {
      const answer = surveyAnswersRaw[id]
      const nonApplicableValue = surveyAnswersRaw[id + nonApplicableSuffix]
      const question = questionsMap[id]
      const notRequired = question == null || question.isRequired === false
      const isNonApplicable =
        nonApplicableValue != null &&
        nonApplicableValue !== '' &&
        !Array.isArray(nonApplicableValue)
      // Skip if question is marek as non applicablle or there is an answer to a obsolete question that's not currently in a template
      if (!isNonApplicable && question != null) {
        if (Array.isArray(answer)) {
          // Treat as non applicable if answer is not filled and is not required
          const skip = !answer.some(q => q) && notRequired
          if (!skip) {
            if (question.type === questionTypes.SELECT_MULTIPLE.id) {
              surveyAnswers[id] = answer
            } else {
              for (let i = 0; i < answer.length; i++) {
                const nonApplicableArray = (
                  nonApplicableValue != null && nonApplicableValue !== '')
                  ? nonApplicableValue
                  : []
                const toCheck = nonApplicableArray[i]
                if ((toCheck == null || toCheck === '') ||
                  answer[i] === 'nonApplicable'
                ) {
                  surveyAnswers[`${id}.${i}`] = answer[i]
                }
              }
            }
          }
        } else {
          // Treat as non applicable if answer is not filled and is not required
          const skip = (answer == null || answer === '') &&
            answer !== 0 && notRequired
          if (!skip) {
            surveyAnswers[id] = answer
          }
        }
      }
    }
  }
  for (const questionId in surveyAnswers) {
    const scoreItem = surveyScoreCard[questionId]
    const scoreGroup: string | undefined =
      scoreItem?.[surveyMapping.group] as string

    if (scoreGroup != null) {
      const { isFilled, isNotFilled, ranges, rangeScoring, multipleAnswers } =
        scoreItem
      const group = normalizeString(scoreGroup)
      const answer = surveyAnswers[questionId]
      const isSelectOptionQuestion = surveyMapping[answer]
      let optionValue = scoreItem[surveyMapping[answer]]

      if (survey.containsAggragatedData) {
        optionValue = answer
      } else {
        if (isSelectOptionQuestion == null || isSelectOptionQuestion === '') {
          if (isFilled != null &&
            isFilled !== '' &&
            isNotFilled != null &&
            isNotFilled !== ''
          ) {
            if (answer !== '' && answer != null) {
              optionValue = Number(isFilled)
            } else if (answer === '' || answer == null) {
              optionValue = Number(isNotFilled)
            }
          } else if (ranges != null && rangeScoring != null) {
            const num = Number(answer)
            const isAnswer = !isNaN(num) && answer !== null && answer !== ''

            if (isAnswer) {
              ranges.some((range, index) => {
                const bool = num < Number(range[1]) && num > Number(range[0])
                if (bool) {
                  optionValue = Number(rangeScoring[index])
                }
                return bool
              })
            }
          } else if (multipleAnswers === true) {
            optionValue = 0
            if (Array.isArray(answer)) {
              answer.forEach((option, index) => {
                const numValue = Number(scoreItem[`Option ${index + 1}`])
                if (option != null &&
                  option !== '' &&
                  option !== false &&
                  !isNaN(numValue)
                ) {
                  (optionValue as number) += numValue
                }
              })
            }
          }
        }
        if (optionValue === 'false') {
          continue
        }
        optionValue = parseFloat((String(optionValue) + '').replace(',', '.'))
      }

      const modifierValue = scoreItem[surveyMapping.modifier]
      const modifierStr = typeof modifierValue === 'string' ? modifierValue : String(modifierValue ?? '')

      const satisfaction =
        normalizeString(modifierStr) === 'satisfaction'
      const weight =
        normalizeString(modifierStr) === 'weighting'

      const section = calc.sections[group]

      if (typeof optionValue === 'number' && !isNaN(optionValue) && section !== undefined) {
        if (weight) {
          section.weight += optionValue
          section.weightTotal += surveyMaxOption(scoreItem)
        } else if (satisfaction) {
          section.satisfaction = (section.satisfaction ?? 0) + optionValue
          section.satisfactionTotal =
            (section.satisfactionTotal ?? 0) + surveyMaxOption(scoreItem)
        } else {
          section.sum = (section.sum ?? 0) + optionValue
          section.total = (section.total ?? 0) + surveyMaxOption(scoreItem)
        }
      }

      let futureValue = calc.Future[group.toLowerCase()]
      if (
        typeof optionValue === 'number' &&
        !isNaN(optionValue) &&
        futureValue !== undefined &&
        typeof futureValue === 'number'
      ) {
        futureValue += optionValue
      }
    }
  }
  let adjusted = 0
  let overall = 0
  let totalWeights = 0
  for (const section in calc.sections) {
    if (!skippedSections.includes(section)) {
      totalWeights += calc.sections[section].weight
    }
  }
  for (const section in calc.sections) {
    const calcSection = calc.sections[section]
    if (!skippedSections.includes(section)) {
      if (calcSection.total != null && calcSection.total > 0) {
        calcSection.percent =
          (calcSection.sum ?? 0) / calcSection.total
      }
      adjusted += (calcSection.percent ?? 0) * calcSection.weight
      overall += (calcSection.percent ?? 0) / 4
      if (totalWeights > 0) {
        calcSection.importancePercent = Number(
          ((calcSection.weight / totalWeights) * 100).toFixed(0)
        )
      }
      if (calcSection.satisfactionTotal != null &&
        calcSection.satisfactionTotal > 0
      ) {
        calcSection.understandingPercent =
          (calcSection.satisfaction ?? 0) /
          calcSection.satisfactionTotal
      }
    }
  }
  if (totalWeights !== 0) {
    calc.adjustment = adjusted / totalWeights
  } else {
    calc.adjustment = overall
  }
  return calc
}

interface CalculatedScoreProps {
  data: CalculatedScore | null
  survey: Survey | null
  skippedSections: string[]
}

export const calculateScores = ({
  data,
  survey,
  skippedSections
}: CalculatedScoreProps): ScoresData => {
  const scoreData: ScoresData = {
    sectionsCount: 0,
    sectionsScoreSum: 0,
    futureScoresSum: 0,
    lowestScore: 1,
    lowestScoreSection: '',
    adjustmentText: null,
    adjustmentValue: null,
    themesAligned: 0,
    overallFutureScore: 0,
    averageScore: 0,
    scoresMatch: 0,
    understanding: ''
  }

  if (data != null) {
    Object.keys(data.sections)
      .filter(
        section => !skippedSections.includes(section) && scoringData[section]
      )
      .forEach((item, index) => {
        const sectionData = data.sections[item]
        if (skippedSections.includes(item)) {
          return null
        }
        scoreData.sectionsCount++
        scoreData.sectionsScoreSum += sectionData.percent ?? 0
        if (sectionData.percent != null &&
          sectionData.percent < scoreData.lowestScore
        ) {
          scoreData.lowestScore = sectionData.percent
          scoreData.lowestScoreSection = scoringData[item].name
        } else if (
          sectionData.percent === scoreData.lowestScore &&
          sectionData.percent !== 1
        ) {
          if (Array.isArray(scoreData.lowestScoreSection)) {
            scoreData.lowestScoreSection.push(scoringData[item].name)
          } else {
            if (scoreData.lowestScoreSection !== '') {
              scoreData.lowestScoreSection = [
                scoreData.lowestScoreSection,
                scoringData[item].name
              ]
            } else {
              scoreData.lowestScoreSection = [scoringData[item].name]
            }
          }
        }
        const adjustmentValue = (data.adjustment * 100).toFixed(0)
        scoreData.adjustmentValue = adjustmentValue
        if (
          sectionData.percent != null &&
          sectionData.understandingPercent != null &&
          Math.abs(sectionData.percent - sectionData.understandingPercent) < 0.25
        ) {
          scoreData.themesAligned++
        }
        scoreData.adjustmentText = survey?.containsAggragatedData === true
          ? (
            <span>
              <Trans>SCORE_CARD_AGGREGATED_IMPORTANCE_ADJUSTED</Trans>{' '}
            </span>
            )
          : (
            <Trans>
              Considering the importance you placed on each of the four key areas,
              this score is adjusted to
            </Trans>
            )
        scoreData.adjustmentPainText = survey?.containsAggragatedData === true
          ? 'SCORE_CARD_AGGREGATED_IMPORTANCE_ADJUSTED'
          : 'Considering the importance you placed on each of the four key areas, this score is adjusted to'
      })
    Object.keys(data.Future).forEach((item, index) => {
      const futureData = data.Future[item] as number
      scoreData.futureScoresSum += futureData
    })
  }
  scoreData.overallFutureScore = (scoreData.futureScoresSum / 6) * 100
  scoreData.averageScore =
    (scoreData.sectionsScoreSum / scoreData.sectionsCount) * 100
  scoreData.scoresMatch = Math.abs(
    scoreData.overallFutureScore - scoreData.averageScore
  )
  scoreData.understanding =
    scoreData.themesAligned <= 1
      ? t`weak`
      : scoreData.themesAligned === 2
        ? t`sufficient`
        : t`good`
  return scoreData
}

export const scoringData = {
  governance: {
    name: t`Governance`,
    scoreTresholds: [
      {
        min: -1,
        text: t`Your organization has a weak handle on your board and management structure. It is recommended that you focus on addressing these issues.`,
        textAggregated: t`SCORE_CARD_GOVERNANCE_KEY_THEME_WEAK_TEXT`
      },
      {
        min: 0.5,
        text: t`Your organization has a reasonable but limited handle on your board and management structure. Consider reflecting on how to improve these processes.`,
        textAggregated: t`SCORE_CARD_GOVERNANCE_KEY_THEME_OK_TEXT`
      },
      {
        min: 0.75,
        text: t`Your organization has a good handle on your board and management structure. Consider sharing your expertise with partner organizations`,
        textAggregated: t`SCORE_CARD_GOVERNANCE_KEY_THEME_GOOD_TEXT`
      }
    ],
    understandingOptions: {
      Aligned: t`Your satisfaction corresponds with your responses. You understand your capacity in governance.`,
      MisalignedLess: t`You may be underestimating your capacity in governance. Please reflect on why your responses do not match your satisfaction.`,
      MisalignedMore: t`You may be overestimating your capacity in governance. Please reflect on why your responses do not match your satisfaction.`
    }
  },
  property: {
    name: t`Property Management`,
    scoreTresholds: [
      {
        min: -1,
        text: t`Your organization has a weak handle on property management. It is recommended that you focus on addressing these issues.`,
        textAggregated: t`SCORE_CARD_PROPERTY_MANAGEMENT_KEY_THEME_WEAK_TEXT`
      },
      {
        min: 0.5,
        text: t` Your organization has a reasonable but limited handle on property management. Consider reflecting on how to improve these processes.`,
        textAggregated: t`SCORE_CARD_PROPERTY_MANAGEMENT_KEY_THEME_OK_TEXT`
      },
      {
        min: 0.75,
        text: t`Your organization has a good handle on property management. Consider sharing your expertise with partner organizations.`,
        textAggregated: t`SCORE_CARD_PROPERTY_MANAGEMENT_KEY_THEME_GOOD_TEXT`
      }
    ],
    understandingOptions: {
      Aligned: t`Your satisfaction corresponds with your responses. You understand your capacity in property management.`,
      MisalignedMore: t`You may be overestimating your capacity in property management. Please reflect on why your responses do not match your satisfaction.`,
      MisalignedLess: t`You may be underestimating your capacity in property management. Please reflect on why your responses do not match your satisfaction.`
    }
  },
  financial: {
    name: t`Financial Managment`,
    scoreTresholds: [
      {
        min: -1,
        text: t`Your organization has a weak handle on financial management. It is recommended that you focus on addressing these issues.`,
        textAggregated: t`SCORE_CARD_FINANCIAL_KEY_THEME_WEAK_TEXT`
      },
      {
        min: 0.5,
        text: t`Your organization has a reasonable but limited handle on financial management. Consider reflecting on how to improve these processes.`,
        textAggregated: t`SCORE_CARD_FINANCIAL_KEY_THEME_OK_TEXT`
      },
      {
        min: 0.75,
        text: t`Your organization has a good handle on financial management. Consider sharing your expertise with partner organizations.`,
        textAggregated: t`SCORE_CARD_FINANCIAL_KEY_THEME_GOOD_TEXT`
      }
    ],
    understandingOptions: {
      Aligned: t`Your satisfaction corresponds with your responses. You understand your capacity in financial management.`,
      MisalignedMore: t`You may be overestimating your capacity in financial management. Please reflect on why your responses do not match your satisfaction.`,
      MisalignedLess: t`You may be underestimating your capacity in financial management. Please reflect on why your responses do not match your satisfaction.`
    }
  },
  social: {
    name: t`Social and community relations`,
    scoreTresholds: [
      {
        min: -1,
        text: t`Your organization has a weak handle on social and community relations. It is recommended that you focus on addressing these issues.`,
        textAggregated: t`SCORE_CARD_SOCIAL_KEY_THEME_WEAK_TEXT`
      },
      {
        min: 0.5,
        text: t`Your organization has a reasonable but limited handle on social and community relations. Consider reflecting on how to improve these processes.`,
        textAggregated: t`SCORE_CARD_SOCIAL_KEY_THEME_OK_TEXT`
      },
      {
        min: 0.75,
        text: t`Your organization has a good handle on social and community relations. Consider sharing your expertise with partner organizations.`,
        textAggregated: t`SCORE_CARD_SOCIAL_KEY_THEME_GOOD_TEXT`
      }
    ],
    understandingOptions: {
      Aligned: t`Your satisfaction corresponds with your responses. You understand your capacity in social and community relations.`,
      MisalignedMore: t`You may be overestimating your capacity in social and community relations. Please reflect on why your responses do not match your satisfaction.`,
      MisalignedLess: t`You may be underestimating your capacity in social and community relations. Please reflect on why your responses do not match your satisfaction.`
    }
  },
  'environmental initatives': {
    name: t`Environmental initatives`,
    scoreTresholds: [
      {
        min: -1,
        text: t`Your organization has an opportunity to make significant improvements in environmental initiatives. It is recommended that you focus on addressing these issues.`,
        textAggregated: t`SCORE_CARD_ENVIRONMENTAL_KEY_THEME_WEAK_TEXT`
      },
      {
        min: 0.5,
        text: t`Your organization has an opportunity to make some improvements in environmental initiatives. Consider reflecting on how to enhance these processes.`,
        textAggregated: t`SCORE_CARD_ENVIRONMENTAL_KEY_THEME_OK_TEXT`
      },
      {
        min: 0.75,
        text: t`You organization demonstrates strong practices in environmental initiatives. Consider sharing your expertise with partner organizations.`,
        textAggregated: t`SCORE_CARD_ENVIRONMENTAL_KEY_THEME_GOOD_TEXT`
      }
    ],
    understandingOptions: {}
  },
  'utility bill analysis': {
    name: t`Utility bill analysis`,
    scoreTresholds: [
      {
        min: -1,
        text: t`Your organization has an opportunity to make significant improvements in utility bill analysis. It is recommended that you focus on addressing these issues.`,
        textAggregated: t`SCORE_CARD_UTILITY_KEY_THEME_WEAK_TEXT`
      },
      {
        min: 0.5,
        text: t`Your organization has an opportunity to make some improvements in utility bill analysis. Consider reflecting on how to enhance these processes.`,
        textAggregated: t`SCORE_CARD_UTILITY_KEY_THEME_OK_TEXT`
      },
      {
        min: 0.75,
        text: t`You organization demonstrates strong practices in utility bill analysis. Consider sharing your expertise with partner organizations.`,
        textAggregated: t`SCORE_CARD_UTILITY_KEY_THEME_GOOD_TEXT`
      }
    ],
    understandingOptions: {}
  },
  'tenant or member education and engagement': {
    name: t`Tenant or member education and engagement`,
    scoreTresholds: [
      {
        min: -1,
        text: t`Your organization has an opportunity to make significant improvements in tenant or member education and engagement. It is recommended that you focus on addressing these issues.`,
        textAggregated: t`SCORE_CARD_TENANT_OR_MEMBER_KEY_THEME_WEAK_TEXT`
      },
      {
        min: 0.5,
        text: t`Your organization has an opportunity to make some improvements in tenant or member education and engagement. Consider reflecting on how to enhance these processes.`,
        textAggregated: t`SCORE_CARD_TENANT_OR_MEMBER_KEY_THEME_OK_TEXT`
      },
      {
        min: 0.75,
        text: t`You organization demonstrates strong practices in tenant or member education and engagement. Consider sharing your expertise with partner organizations.`,
        textAggregated: t`SCORE_CARD_TENANT_OR_MEMBER_KEY_THEME_GOOD_TEXT`
      }
    ],
    understandingOptions: {}
  },
  maintenance: {
    name: t`Maintenance`,
    scoreTresholds: [
      {
        min: -1,
        text: t`Your organization has an opportunity to make significant improvements in maintenance. It is recommended that you focus on addressing these issues.`,
        textAggregated: t`SCORE_CARD_MAINTENANCE_KEY_THEME_WEAK_TEXT`
      },
      {
        min: 0.5,
        text: t`Your organization has an opportunity to make some improvements in maintenance. Consider reflecting on how to enhance these processes.`,
        textAggregated: t`SCORE_CARD_MAINTENANCE_KEY_THEME_OK_TEXT`
      },
      {
        min: 0.75,
        text: t`You organization demonstrates strong practices in maintenance. Consider sharing your expertise with partner organizations.`,
        textAggregated: t`SCORE_CARD_MAINTENANCE_KEY_THEME_GOOD_TEXT`
      }
    ],
    understandingOptions: {}
  },
  'eco-responsability': {
    name: t`Eco-responsability`,
    scoreTresholds: [
      {
        min: -1,
        text: t`Your organization has an opportunity to make significant improvements in eco-responsibility. It is recommended that you focus on addressing these issues.`,
        textAggregated: t`SCORE_CARD_ECORESPONSIBILITY_KEY_THEME_WEAK_TEXT`
      },
      {
        min: 0.5,
        text: t`Your organization has an opportunity to make some improvements in eco-responsibility. Consider reflecting on how to enhance these processes.`,
        textAggregated: t`SCORE_CARD_ECORESPONSIBILITY_KEY_THEME_OK_TEXT`
      },
      {
        min: 0.75,
        text: t`You organization demonstrates strong practices in eco-responsibility. Consider sharing your expertise with partner organizations.`,
        textAggregated: t`SCORE_CARD_ECORESPONSIBILITY_KEY_THEME_GOOD_TEXT`
      }
    ],
    understandingOptions: {}
  }
}
